// churnZones.js
// Per-candle churn zones (LuxAlgo "Churn" idea + footprint delta balance).
// A churn bar: volume >= relVolMin x 20-bar avg, range <= rangeRatio x the
// range expected at that relVol (log-log fit over the prior fitLen bars),
// and buy/sell roughly balanced. Churn bars within mergeGap bars merge into
// one zone (high/low box). A zone resolves on a close beyond it with a
// range >= avgRange, or a close a full avgRange beyond. Unresolved zones
// expire expireBars after their last churn bar.
// Closed candles only (live bar excluded) - compute once per structural change.

import { getCandleHL, getCandleDelta } from './swingUtils'
import { forEachLevel } from './candleHelpers'

export const CHURN_ZONE_DEFAULTS = {
  volLen: 20, fitLen: 100, minFit: 20, relVolMin: 1.5,
  rangeRatio: 0.7, balanceMin: 0.6, mergeGap: 3, expireBars: 40, breakoutMult: 1.0,
  upCol: '#26de81', downCol: '#fc5c65', noneCol: '#969696', opacity: 0.10, showUnresolved: true,
  style: 'boxes',   // 'boxes' | 'background' (tint the footprint column plates)
}

/** Clamp a stored/partial config into a safe full config. */
export function normalizeChurnCfg(c) {
  const d = CHURN_ZONE_DEFAULTS, o = { ...d, ...(c || {}) }
  const num = (v, def, lo, hi) => { const x = Number(v); return Number.isFinite(x) ? Math.max(lo, Math.min(hi, x)) : def }
  return {
    ...o,
    volLen: Math.round(num(o.volLen, d.volLen, 2, 500)),
    fitLen: Math.round(num(o.fitLen, d.fitLen, 10, 1000)),
    relVolMin: num(o.relVolMin, d.relVolMin, 0.1, 20),
    rangeRatio: num(o.rangeRatio, d.rangeRatio, 0.05, 2),
    balanceMin: num(o.balanceMin, d.balanceMin, 0, 1),
    mergeGap: Math.round(num(o.mergeGap, d.mergeGap, 0, 50)),
    expireBars: Math.round(num(o.expireBars, d.expireBars, 1, 1000)),
    breakoutMult: num(o.breakoutMult, d.breakoutMult, 0, 10),
    opacity: num(o.opacity, d.opacity, 0, 1),
    showUnresolved: o.showUnresolved !== false,
    style: o.style === 'background' ? 'background' : 'boxes',
  }
}

function rgba(hex, a) {
  const m = /^#?([0-9a-f]{6})/i.exec(String(hex || ''))
  const v = m ? parseInt(m[1], 16) : 0x969696
  return `rgba(${(v >> 16) & 255},${(v >> 8) & 255},${v & 255},${Math.max(0, Math.min(1, a))})`
}

// Volume / delta per candle. Real candle shapes (no buy_volume/sell_volume
// anywhere in the app - that was the bug: vol=0 on every historical candle,
// so relVol and balance were 0 and nothing ever qualified):
//   live            -> _liveLevels via forEachLevel (buy/sell per level)
//   OM footprint    -> _nativeTotalVol / _nativeDelta
//   kline history   -> volume_tokens only, no side split (balance unknown -> pass)
function candleStats(c) {
  const { high, low } = getCandleHL(c)
  let vol = 0, delta = null, close = c.close_mcap
  if (Number.isFinite(c.buy_volume) || Number.isFinite(c.sell_volume)) {
    vol = (c.buy_volume || 0) + (c.sell_volume || 0); delta = (c.buy_volume || 0) - (c.sell_volume || 0)
  } else if (c._nativeTotalVol > 0) {
    vol = c._nativeTotalVol; delta = Number.isFinite(c._nativeDelta) ? c._nativeDelta : null
  } else if (!c.isHistorical) {
    let v = 0, w = 0, d = 0
    try { forEachLevel(c, (m, b, s) => { const t = (b || 0) + (s || 0); v += t; w += m * t; d += (b || 0) - (s || 0) }) } catch {}
    vol = v; delta = v > 0 ? d : null
    if (close == null && v > 0) close = w / v
  }
  if (!(vol > 0)) vol = Number(c.volume_tokens) || Number(c.volume) || 0
  if (close == null || !Number.isFinite(close)) close = (high + low) / 2
  const balance = delta == null ? 1 : (vol > 0 ? 1 - Math.min(1, Math.abs(delta) / vol) : 0)
  return { high, low, close, vol, range: Math.max(0, high - low), balance }
}

/** Debug counters from the LAST computeChurnZones run (any chart). Per-chart
 *  stats ride on the returned array as zones.stats - use those. */
export const churnStats = { total: 0, n: 0, volZero: 0, relVolPass: 0, balancePass: 0, rangePass: 0, fitFallback: 0, zones: 0, skippedReason: null }

const MIN_FIT_SAMPLES = 10

/** @returns {Array<{start,end,lastChurn,hi,lo,dir:'up'|'down'|null,resolvedIdx,expired}>} (+ .stats) */
export function computeChurnZones(candles, cfg = CHURN_ZONE_DEFAULTS) {
  const o = normalizeChurnCfg(cfg)
  const total = Array.isArray(candles) ? candles.length : 0
  const n = Math.max(0, total - 1)              // skip live bar
  const stats = { total, n, volZero: 0, relVolPass: 0, balancePass: 0, rangePass: 0, fitFallback: 0, zones: 0, skippedReason: null }
  const zones = []
  const done = () => { Object.assign(churnStats, stats); Object.defineProperty(zones, 'stats', { value: stats, enumerable: false }); return zones }
  // Degrade gracefully: start once a volume baseline exists. Short history
  // shrinks the volume window instead of blocking everything.
  const volLen = Math.max(2, Math.min(o.volLen, n - 5))
  const need = Math.max(o.volLen, 10) + 5
  if (n < need) { stats.skippedReason = `need >= ${need} closed candles, have ${n}`; return done() }
  stats.volLen = volLen
  const minFit = Math.max(MIN_FIT_SAMPLES, Math.min(o.minFit, o.fitLen))

  const st = new Array(n)
  for (let i = 0; i < n; i++) { st[i] = candleStats(candles[i]); if (!(st[i].vol > 0)) stats.volZero++ }
  if (stats.volZero >= n) { stats.skippedReason = 'no volume on any candle'; return done() }

  const relVol = new Array(n).fill(0)
  const avgRange = new Array(n).fill(0)
  let vs = 0, rs = 0
  for (let i = 0; i < n; i++) {
    if (i >= volLen) {
      const av = vs / volLen
      relVol[i] = av > 0 ? st[i].vol / av : 0
      avgRange[i] = rs / volLen
    }
    vs += st[i].vol; rs += st[i].range
    if (i >= volLen) { vs -= st[i - volLen].vol; rs -= st[i - volLen].range }
  }

  let open = null
  for (let i = volLen; i < n; i++) {
    const s = st[i]
    if (open && i > open.lastChurn) {
      const ar = avgRange[i] * o.breakoutMult
      if (s.close > open.hi && (s.range >= ar || s.close >= open.hi + ar)) { open.dir = 'up'; open.resolvedIdx = i; open = null }
      else if (s.close < open.lo && (s.range >= ar || s.close <= open.lo - ar)) { open.dir = 'down'; open.resolvedIdx = i; open = null }
      else if (i - open.lastChurn > o.expireBars) { open.expired = true; open = null }
    }
    const rv = relVol[i]
    if (!(rv >= o.relVolMin) || !(s.range > 0)) continue
    stats.relVolPass++
    if (s.balance < o.balanceMin) continue
    stats.balancePass++
    // expected range at this relVol: log-log fit over prior bars, or (too few
    // samples) the plain average range.
    let k = 0, sx = 0, sy = 0, sxx = 0, sxy = 0
    for (let j = Math.max(volLen, i - o.fitLen); j < i; j++) {
      if (!(relVol[j] > 0) || !(st[j].range > 0)) continue
      const x = Math.log(relVol[j]), y = Math.log(st[j].range)
      k++; sx += x; sy += y; sxx += x * x; sxy += x * y
    }
    let expected
    if (k >= minFit) {
      const den = k * sxx - sx * sx
      const b = Math.abs(den) > 1e-12 ? (k * sxy - sx * sy) / den : 0
      const a0 = (sy - b * sx) / k
      expected = Math.exp(a0 + b * Math.log(rv))
    } else {
      stats.fitFallback++
      expected = avgRange[i]
    }
    if (!Number.isFinite(expected) || !(expected > 0) || s.range > o.rangeRatio * expected) continue
    stats.rangePass++
    if (open && i - open.lastChurn <= o.mergeGap) {
      open.end = i; open.lastChurn = i
      if (s.high > open.hi) open.hi = s.high
      if (s.low < open.lo) open.lo = s.low
    } else {
      open = { start: i, end: i, lastChurn: i, hi: s.high, lo: s.low, dir: null, resolvedIdx: null, expired: false }
      zones.push(open)
    }
  }
  stats.zones = zones.length
  if (!zones.length) stats.skippedReason = stats.relVolPass === 0 ? 'no candle reached relVolMin' : stats.balancePass === 0 ? 'balance check failed on all' : 'range check failed on all'
  return done()
}


// Footprint plate base colour (FootprintChartCanvas cellSlotBg: rgba(48,52,64,0.92)).
const PLATE_RGB = [48, 52, 64], PLATE_A = 0.92
function hexRgb(hex) {
  const m = /^#?([0-9a-f]{6})/i.exec(String(hex || ''))
  const v = m ? parseInt(m[1], 16) : 0x969696
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255]
}
/** Background mode: Map candleIdx -> blended plate colour (zone start..end;
 *  unresolved colour until the zone resolves, then up/down). Tint strength =
 *  opacity setting. Returns { map, key } (key feeds the geometry cache). */
export function buildChurnBgMap(zones, style) {
  const st = normalizeChurnCfg(style)
  const map = new Map()
  if (!zones || !zones.length) return { map, key: '' }
  const t = st.opacity
  const mix = (hex) => {
    const c = hexRgb(hex)
    const r = Math.round(PLATE_RGB[0] + (c[0] - PLATE_RGB[0]) * t)
    const g = Math.round(PLATE_RGB[1] + (c[1] - PLATE_RGB[1]) * t)
    const b = Math.round(PLATE_RGB[2] + (c[2] - PLATE_RGB[2]) * t)
    return `rgba(${r}, ${g}, ${b}, ${PLATE_A})`
  }
  const cols = { up: mix(st.upCol), down: mix(st.downCol), none: mix(st.noneCol) }
  let key = `${t}|${st.upCol}|${st.downCol}|${st.noneCol}|${st.showUnresolved ? 1 : 0}`
  for (const z of zones) {
    if (!z.dir && !st.showUnresolved) continue
    const c = cols[z.dir || 'none']
    for (let i = z.start; i <= z.end; i++) map.set(i, c)
    key += `|${z.start}-${z.end}${z.dir ? z.dir[0] : 'n'}`
  }
  return { map, key }
}

/** Draw zones as light boxes. xMid(i)->px, yOf(price)->px, cw = candle px width. */
// dimFill(z) (optional): true -> that zone's fill at half alpha, outline as
// normal (hold-hover-alpha-20261016: the mouse-hovered zone).
export function drawChurnZones(ctx, zones, { startIdx, endIdx, xMid, yOf, cw, lastIdx }, style, dimFill) {
  if (!zones || !zones.length) return
  const st = normalizeChurnCfg(style)
  const col = { up: st.upCol, down: st.downCol, none: st.noneCol }
  const edgeA = Math.min(1, st.opacity * 4 + 0.05)
  ctx.save()
  ctx.lineWidth = 1
  for (const z of zones) {
    if (!z.dir && !st.showUnresolved) continue
    const stop = z.resolvedIdx ?? (z.expired ? z.lastChurn + st.expireBars : lastIdx)
    if (stop < startIdx || z.start > endIdx) continue
    const key = z.dir || 'none'
    const x0 = xMid(z.start) - cw / 2, x1 = xMid(stop) + cw / 2
    const y0 = yOf(z.hi), y1 = yOf(z.lo)
    const top = Math.min(y0, y1), h = Math.max(1, Math.abs(y1 - y0))
    ctx.fillStyle = rgba(col[key], st.opacity * (dimFill && dimFill(z) ? 0.5 : 1)); ctx.fillRect(x0, top, x1 - x0, h)
    ctx.strokeStyle = rgba(col[key], edgeA); ctx.strokeRect(Math.round(x0) + 0.5, Math.round(top) + 0.5, Math.round(x1 - x0), Math.round(h))
  }
  ctx.restore()
}